import { AnnotationPlane } from '~/domain/annotation/annotation';
import {
    angleDegAtVertex,
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    createMeasurementId,
    Measurement,
    MeasurementDraft,
    measurementDraftInFrame,
    measurementInFrame,
    MeasurementKind,
    MEASUREMENT_POINT_COUNT,
} from '~/domain/measurement/measurement';
import type { ShownPaneImage } from '~/engine/view/paneImage';
import { paneToImageFrame } from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { AnnotationPlacementTool } from './AnnotationPlacementTool';

/**
 * Two points that resolve to within this distance (mm) of each other are
 * treated as the same point. This only debounces a click on a spot already
 * placed: it prevents a zero-length distance and an angle with a side of no
 * length, without rejecting legitimately short measurements, which are always
 * far larger than this sub-pixel distance. Not to be confused with the distance
 * that tells a click from a drag, which is measured on screen.
 */
const COINCIDENT_POINT_EPSILON_MM = 1e-4;

/** The placement in the pane's frame, with the pane it was started on. */
interface ActiveDraft {
    readonly pane: ShownPaneImage;
    readonly plane: AnnotationPlane;
    readonly points: PointMm[];
    hoverPointMm: PointMm | null;
}

/**
 * Engine tool for placing distance (2-point) and angle (3-point)
 * measurements directly on the displayed image, in any MPR view.
 *
 * Each left click drops a point on the current slice, and a press-drag-release
 * drops the next one where it is released; when the required number of points
 * is reached the measurement is committed and the tool returns to idle, so the
 * **next click starts a brand-new measurement with no tool switch**. The tool owns only the transient draft (mirrored to recoil
 * via {@link _onDraftChanged} for the SVG overlay); committed measurements go
 * out through {@link _onCommit} and live in recoil, so they survive tool
 * switches and the per-enter/leave activation cycle.
 *
 * The click-to-place lifecycle itself lives in {@link AnnotationPlacementTool}.
 */
export class MeasurementTool extends AnnotationPlacementTool {
    public readonly toolType = 'measurement';

    private readonly _mode: MeasurementKind;
    private readonly _onDraftChanged: (draft: MeasurementDraft | null) => void;
    private readonly _onCommit: (measurement: Measurement) => void;

    private _draft: ActiveDraft | null = null;

    public constructor(
        mode: MeasurementKind,
        onDraftChanged: (draft: MeasurementDraft | null) => void,
        onCommit: (measurement: Measurement) => void
    ) {
        super();
        this._mode = mode;
        this._onDraftChanged = onDraftChanged;
        this._onCommit = onCommit;
    }

    /** Accepts the drag gesture as well as click-to-place. */
    protected override placesPointOnDragRelease(): boolean {
        return true;
    }

    protected override startPlacement(
        _view: PlaneView,
        plane: AnnotationPlane,
        pointMm: PointMm,
        pane: ShownPaneImage
    ): boolean {
        this._draft = { pane, plane, points: [pointMm], hoverPointMm: null };
        this.emitDraft();
        return true;
    }

    protected override placePoint(_view: PlaneView, pointMm: PointMm): void {
        const draft = this._draft;
        if (!draft) {
            return;
        }
        // Debounce a click on any spot already placed, not only the last one:
        // an angle whose far point lands on its first would have no value.
        if (
            draft.points.some(
                (placed) =>
                    euclideanDistanceMm(placed, pointMm) <
                    COINCIDENT_POINT_EPSILON_MM
            )
        ) {
            return;
        }
        draft.points.push(pointMm);
        draft.hoverPointMm = null;

        if (draft.points.length >= MEASUREMENT_POINT_COUNT[this._mode]) {
            this.commitDraft(draft);
        } else {
            this.emitDraft();
        }
    }

    protected override moveCursor(_view: PlaneView, pointMm: PointMm): void {
        if (!this._draft) {
            return;
        }
        this._draft.hoverPointMm = pointMm;
        this.emitDraft();
    }

    protected override undoPoint(): void {
        if (!this._draft) {
            return;
        }
        this._draft.points.pop();
        if (this._draft.points.length === 0) {
            this.cancelDraft();
        } else {
            this.emitDraft();
        }
    }

    protected override lastPlacedPoint(): PointMm | null {
        const draft = this._draft;
        return draft ? draft.points[draft.points.length - 1] : null;
    }

    protected override discardDraft(): void {
        this._draft = null;
        this._onDraftChanged(null);
    }

    private commitDraft(draft: ActiveDraft): void {
        const measurement = this.buildMeasurement(draft);
        // Reset to idle BEFORE notifying so the next click starts fresh.
        this._draft = null;
        this.finishPlacement();
        this._onDraftChanged(null);
        if (measurement) {
            this._onCommit(
                measurementInFrame(measurement, paneToImageFrame(draft.pane))
            );
        }
    }

    private buildMeasurement(draft: ActiveDraft): Measurement | null {
        const base = {
            id: createMeasurementId(),
            plane: draft.plane,
            visible: true,
        };
        if (this._mode === 'distance') {
            const [a, b] = draft.points;
            return {
                ...base,
                kind: 'distance',
                points: [a, b],
                distanceMm: euclideanDistanceMm(a, b),
            };
        }
        const [armA, vertex, armB] = draft.points;
        const angleDeg = angleDegAtVertex(armA, vertex, armB);
        // Guarded by the coincident-point debounce, so this is defensive:
        // never store a non-finite angle.
        if (!Number.isFinite(angleDeg)) {
            return null;
        }
        return {
            ...base,
            kind: 'angle',
            points: [armA, vertex, armB],
            angleDeg,
        };
    }

    private emitDraft(): void {
        if (!this._draft) {
            return;
        }
        this._onDraftChanged(
            measurementDraftInFrame(
                {
                    kind: this._mode,
                    plane: this._draft.plane,
                    points: [...this._draft.points],
                    hoverPointMm: this._draft.hoverPointMm,
                },
                paneToImageFrame(this._draft.pane)
            )
        );
    }
}
